import './index.less';

import { ToastContainer } from '@bfe/moly';
import React, { useLayoutEffect, useMemo, useState } from 'react';

import SettingGroup from '@/components/SettingGroup';
import { useI18nContext } from '@/i18n/I18n';
import { useDeadPoolStore } from '@/store/useDeadPool';
import { useStore } from '@/store/useStore';
import { useUserStore } from '@/store/useUser';
import { complianceHide, complianceShow } from '@/utils/complianceHelpers';

import AntiPhishingCode from './comps/AntiPhishingCodeComps';
import EmailAuth from './comps/emailAuth/EmailAuth';
import FundPassword from './comps/FundPassword';
import GoogleAuth from './comps/GoogleAuth';
import ModifyPassword from './comps/modifyPassword/ModifyPassword';
import Passkey from './comps/passkey';
import PhoneAuth from './comps/phoneAuth/PhoneAuth';
import PrimaryDevice from './comps/PrimaryDevice';
import RiskAssessment from './comps/RiskAssets';
import FetchCoinSecurity from './fetchCoinSecurity';
import GuideModifyPassword from './guide/guide-resetpassword';

const LEVELS = {
  levelDefault: '0',
  level1: '1',
  level2: '2',
  level3: '3',
};

const componentGroups: {
  title: string;
  key: string;
  components: {
    key: string;
    element: any;
    kycHidden?: string;
    subMemberHidden?: boolean;
    complianceHiddenConfig?: (config: any) => boolean;
    deadPoolHiddenConfig?: (
      config: any,
      deadPoolLoading: boolean,
      uid: number,
    ) => boolean;
  }[];
}[] = [
  // 双重验证
  {
    title: 'twoFactorAuth',
    key: 'Two-factor-authentication',
    components: [
      {
        key: 'ModifyPassword',
        element: ModifyPassword,
        subMemberHidden: true,
      },
      {
        key: 'EmailAuth',
        element: EmailAuth,
        subMemberHidden: true,
      },
      {
        key: 'PhoneAuth',
        element: PhoneAuth,
        subMemberHidden: true,
      },
      {
        key: 'GoogleAuth',
        element: GoogleAuth,
      },
    ],
  },
  // 高级安全设置
  {
    title: 'accountSenoirSecurity',
    key: 'Advanced Protection',
    components: [
      {
        key: 'Passkey',
        element: Passkey,
        subMemberHidden: true,
        complianceHiddenConfig: (config: any) => {
          return complianceHide(config?.passkey);
        },
      },
      {
        key: 'MainDevice',
        element: PrimaryDevice,
        subMemberHidden: true,
        complianceHiddenConfig: (config: any) => {
          return complianceHide(config?.mainDevice);
        },
        deadPoolHiddenConfig: (
          config: any,
          deadPoolLoading: boolean,
          uid: number,
        ) => {
          if (deadPoolLoading) {
            return true;
          }
          try {
            const tails = JSON.parse(
              config?.['cht.userAccountGray.config']?.value,
            )?.primaryDevice?.uid;
            const hitGray = tails.some((tailStr: string) => {
              const tailLength = tailStr.length;
              return `${uid}`.slice(-tailLength) === tailStr;
            });
            return !hitGray;
          } catch (error) {
            return true;
          }
        },
      },
      {
        key: 'FundPassword',
        element: FundPassword,
        subMemberHidden: true,
      },
      {
        key: 'AntiPhishingCode',
        element: AntiPhishingCode,
        subMemberHidden: true,
      },
    ],
  },
  // 交易安全设置
  {
    title: 'transactionSecurity',
    key: 'Transaction Security',
    components: [
      {
        key: 'RiskAssessment',
        element: RiskAssessment,
        kycHidden: LEVELS.levelDefault,
        complianceHiddenConfig: (config: any) => {
          return !complianceShow(config?.RiskAsset);
        },
      },
    ],
  },
  // 提币安全
  {
    title: 'fetchCoinSecurityNewTip',
    key: 'fetchCoinSecurityNewTip',
    components: [
      {
        key: 'FetchCoinSecurity',
        element: FetchCoinSecurity,
        subMemberHidden: true,
      },
    ],
  },
];

const Security = ({ loadFn }: { loadFn: () => void }) => {
  const [$t] = useI18nContext();
  const {
    userRole: { isSubmember },
    kyc,
  } = useStore();
  const [visible, setVisible] = useState(
    localStorage.getItem('guide_edit_pwd2') === '1',
  );

  const { complianceConfig, profile } = useUserStore();
  const { deadPoolConfig, loading: deadPoolLoading } = useDeadPoolStore();

  useLayoutEffect(() => {
    loadFn();
  }, []);

  const level = useMemo(() => {
    const isEnterprise =
      profile?.member_tag &&
      profile?.member_tag.KYC &&
      profile?.member_tag.KYC === '1';
    if (isEnterprise) return LEVELS.level3;
    const levels: { [key: string]: string } = {
      LEVEL_DEFAULT: LEVELS.levelDefault,
      LEVEL_1: LEVELS.level1,
      LEVEL_2: LEVELS.level2,
    };

    return levels[kyc?.applicant?.level] || LEVELS.levelDefault;
  }, [profile, kyc]);

  return (
    <>
      {/* 弹窗：系统检测到您的账户存在安全风险。为保证您的账户安全，建议您立即修改登录密码。 */}
      {visible && <GuideModifyPassword onCancel={() => setVisible(false)} />}
      {componentGroups.map(({ title, components, key }) => {
        const visibleComponents = components.filter((component) => {
          let hiddenSubMember = false;
          let hiddenCompliance = false;
          let hiddenKyc = typeof component.kycHidden !== 'undefined'; // 默认隐藏
          let hiddenDeadPool = false; // 根据deadpool隐藏

          // 如果子账号登录需要隐藏时
          if (component.subMemberHidden) {
            // 判断是否为子账号
            hiddenSubMember = isSubmember;
          }
          if (typeof component.complianceHiddenConfig === 'function') {
            hiddenCompliance =
              component.complianceHiddenConfig(complianceConfig);
          }

          if (component.kycHidden) {
            hiddenKyc = level === LEVELS.levelDefault;
          }

          if (typeof component.deadPoolHiddenConfig === 'function') {
            hiddenDeadPool = component.deadPoolHiddenConfig(
              deadPoolConfig,
              deadPoolLoading,
              profile.id,
            );
          }
          return (
            !hiddenSubMember &&
            !hiddenCompliance &&
            !hiddenKyc &&
            !hiddenDeadPool
          );
        });

        if (visibleComponents.length === 0) {
          return null;
        }

        return (
          <SettingGroup
            className="security-setting-group"
            key={key}
            title={title ? $t(title) : ''}
          >
            {visibleComponents.map(({ key, element: Element }) => (
              <Element key={key} />
            ))}
          </SettingGroup>
        );
      })}
      <ToastContainer />
    </>
  );
};
export default Security;
